import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { Typography } from "@progress/kendo-react-common";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import FormTextField from "../../components/formFields/FormTextField";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import ShadowCard from "../../components/common/ShadowCard";
import {
  createVesselsExportLoading,
  getVesselsExportLoadingByID,
  updateVesselsExportLoading,
} from "./services/vesselsExportLoading.services";
import { clearVesselsExportLoadingDetails } from "./vesselsExportLoadingSlice";
import FormDatePicker from "../../components/formFields/FormDateField";
import { closeDialog } from "../../components/dialog/dialogSlice";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { getAllActiveNewPort } from "../newport/services/newport.services";
import { getAllActiveAgent } from "../agent/services/agent.services";
import { getAllActiveLine } from "../line/services/line.services";
import { INewPort } from "../newport/newportModel";
import { IAgent } from "../agent/agentModel";
import FormMultiSelectionFiled from "../../components/formFields/FormMultiSelectionFiled";
import moment from "moment";

const CreateVesselsExportLoading: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const shipping_guid = location.state?.shipping_guid;
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state) => state.vesselsExportLoading.loading);
  const AgentList = useAppSelector((state) => state.agent.AgentList);
  const PortList = useAppSelector((state) => state.newport.NewPortList);

  const VesselsExportLoadingDetail = useAppSelector(
    (state) => state.vesselsExportLoading.VesselsExportLoadingDetail
  );
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    dispatch(getAllActiveNewPort());
    dispatch(getAllActiveAgent());
    dispatch(getAllActiveLine());
    return () => {
      dispatch(clearVesselsExportLoadingDetails());
      dispatch(closeDialog());
    };
  }, []);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [VesselsExportLoadingDetail, shipping_guid]);

  useEffect(() => {
    if (shipping_guid) {
      dispatch(getVesselsExportLoadingByID(shipping_guid));
    }
  }, [shipping_guid]);

  const handleSubmit = async (values: any) => {
    const formdata = {
      // form_type: "VESSELS",
      eta: values?.eta ? moment(values?.eta).format("YYYY-MM-DD") : "",
      vessels_name: values?.vessels_name,
      agent_id: values?.agent_id.join(","),
      etd: values?.etd ? moment(values?.etd).format("YYYY-MM-DD") : "",
      at_jetty: values?.at_jetty,
      cargo: values?.cargo,
      next_port_id: values?.next_port_id,
    };
    if (shipping_guid) {
      try {
        const updatePayload = {
          shipping_guid: shipping_guid,
          ...formdata,
        };

        const response = await dispatch(
          updateVesselsExportLoading(updatePayload)
        );
        if (response?.meta?.requestStatus === "fulfilled") {
          navigate("/shippingmovements");
        }
      } catch (error) {
        console.error("Error in handleSubmit:", error);
        throw error;
      }
    } else {
      try {
        const response = await dispatch(createVesselsExportLoading(formdata));
        if (response?.meta?.requestStatus === "fulfilled") {
          navigate("/shippingmovements");
        }
      } catch (error) {
        console.error("Error in handleSubmit:", error);
        throw error;
      }
    }
  };

  if (loading) return <LoadingPanel gridRef={gridRef} />;

  return (
    <>
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={VesselsExportLoadingDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <ShadowCard style={{ padding: 12 }}>
              <GridLayout
                style={{ marginRight: 30 }}
                gap={{ rows: 10, cols: 10 }}
                cols={[
                  { width: "25%" },
                  { width: "25%" },
                  { width: "25%" },
                  { width: "25%" },
                ]}
              >
                <GridLayoutItem colSpan={4}>
                  <Typography.h4>
                    {shipping_guid
                      ? "Update Vessel's Export Loading"
                      : "Add Vessel's Export Loading"}
                  </Typography.h4>
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="at_jetty"
                    label="At Jetty"
                    placeholder="i.e.APPPL"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="eta"
                    label="ETA"
                    component={FormDatePicker}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="etd"
                    label="ETD"
                    component={FormDatePicker}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="vessels_name"
                    label="Vessel's Name"
                    placeholder="Vessels Name"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="agent_id"
                    label="Agent"
                    placeholder="Select Agent"
                    component={FormMultiSelectionFiled}
                    options={AgentList?.map((agent: IAgent) => {
                      return {
                        value: agent.id,
                        label: agent.agent_name,
                      };
                    })}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="cargo"
                    label="Cargo"
                    placeholder="Cargo"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="next_port_id"
                    label="Next Port"
                    placeholder="Select Port"
                    component={FormSelectionField}
                    options={PortList?.map((port: INewPort) => {
                      return {
                        value: port.id,
                        label: port.port_name,
                      };
                    })}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem
                  colSpan={4}
                  style={{
                    textAlign: "end",
                    marginTop: "20px",
                    width: "100%",
                  }}
                >
                  <ButtonWithLoading
                    label={shipping_guid ? "Update" : "Save"}
                    type="submit"
                    disabled={!formRenderProps.allowSubmit || loading}
                    loading={loading}
                  />
                  <Button
                    type="button"
                    fillMode={"outline"}
                    themeColor={"primary"}
                    style={{ marginLeft: 4 }}
                    onClick={() => navigate("/shippingmovements")}
                  >
                    Cancel
                  </Button>
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateVesselsExportLoading;
